import { useEffect, useMemo, useRef } from 'react';
import Container from '@components/global/Container';
import cn from 'classnames';
import Heading from '@components/ui/Heading';
import Image from 'next/image';
import Button from '@components/ui/Button';
import { Copy, PrismicImage } from '@adapters/prismic/types/brandedStorePage';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
  EnhancedPlpBannerEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';
import useObjectVisibility from '@hooks/useObjectVisibility';

import styles from './EnhancedPlpBanner.module.scss';

interface EnhancedPlpBannerProps {
  title: string;
  copy: string;
  markdownCopy: Copy;
  ctaText?: string;
  ctaUrl?: string;
  desktopImage: PrismicImage;
  mobileImage: PrismicImage;
  brandedStoreCoopAttribute?: string | null;
  textOnRight?: boolean;
  noH1?: boolean;
  sliceIndex?: number;
  interactionLocation?:
    | typeof InteractionLocation.EnhancedPlpBanner
    | typeof InteractionLocation.ImageText;
}

const EnhancedPlpBanner = ({
  title,
  copy,
  markdownCopy,
  ctaText,
  ctaUrl,
  desktopImage,
  mobileImage,
  brandedStoreCoopAttribute,
  textOnRight,
  noH1,
  sliceIndex,
  interactionLocation = InteractionLocation.EnhancedPlpBanner,
}: EnhancedPlpBannerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      page: {
        path: typeof window === 'undefined' ? '' : window.location.pathname,
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.EnhancedPlpBanner,
      },
      ecommerce: {
        creative_name: `image,${desktopImage?.url || ''}`,
        creative_slot: buildCreativeSlotName({
          interactionLocation,
          sliceIndex,
        }),
        promotion_id: title || null,
        promotion_name: ctaText || null,
        isVideo: false,
      },
    }),
    [
      brandedStoreCoopAttribute,
      ctaText,
      desktopImage?.url,
      interactionLocation,
      sliceIndex,
      title,
    ]
  );

  useEffect(() => {
    if (isVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...promoDetails,
      } as EnhancedPlpBannerEventProps);
    }
  }, [isVisible, promoDetails]);

  const handleCTAClick = () => {
    trackPromotionEvent({
      eventName: PromotionEventName.selectPromotion,
      ...promoDetails,
    } as EnhancedPlpBannerEventProps);
  };

  return (
    <Container>
      <div
        className={cn(styles.container, {
          [styles.textOnRight]: textOnRight,
        })}
        ref={ref}
      >
        <div
          className={cn(styles.content, {
            [styles.width]: noH1,
          })}
        >
          <Heading
            semanticLevel={noH1 ? 'h2' : 'h1'}
            styleLevel={noH1 ? '2' : '1'}
          >
            {title}
          </Heading>
          {markdownCopy?.[0]?.text ? (
            <MarkdownCopy
              text={markdownCopy?.[0]?.text ?? ''}
              className={styles.copy}
            />
          ) : (
            <p>{copy}</p>
          )}
          {ctaText && ctaUrl && (
            <Button
              href={ctaUrl}
              label={ctaText}
              size={'large'}
              variant={'primary'}
              handleClick={handleCTAClick}
            />
          )}
        </div>
        <div className={styles.imageContainer}>
          <div className={styles.desktopImageContainer}>
            <Image
              src={desktopImage?.url ?? ''}
              alt={desktopImage?.alt ?? ''}
              layout={'responsive'}
              width={desktopImage?.dimensions?.width}
              height={desktopImage?.dimensions?.height}
              priority
            />
          </div>
          <div className={styles.mobileImageContainer}>
            <Image
              src={mobileImage?.url ?? ''}
              alt={mobileImage?.alt ?? ''}
              layout={'responsive'}
              width={mobileImage?.dimensions?.width}
              height={mobileImage?.dimensions?.height}
              priority
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default EnhancedPlpBanner;
